import React, { useState, useEffect, useCallback } from 'react';
import { X, CaretLeft, CaretRight, DownloadSimple, MagnifyingGlassPlus, MagnifyingGlassMinus } from '@phosphor-icons/react';

export const Lightbox = ({ images, initialIndex = 0, onClose }) => {
  const [index, setIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);

  const next = useCallback(() => { setIndex(i => (i + 1) % images.length); setZoom(1); }, [images.length]);
  const prev = useCallback(() => { setIndex(i => (i - 1 + images.length) % images.length); setZoom(1); }, [images.length]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, next, prev]);

  const currentImage = images[index];
  const src = typeof currentImage === 'string' ? currentImage : currentImage?.url || currentImage?.src || '';
  const caption = typeof currentImage === 'object' ? currentImage?.caption || currentImage?.title || '' : '';

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col" data-testid="lightbox-overlay" onClick={onClose}>
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 flex-shrink-0" onClick={e => e.stopPropagation()}>
        <p className="text-white/70 text-sm font-bold">{index + 1} / {images.length}</p>
        <div className="flex items-center gap-2">
          <button onClick={() => setZoom(z => Math.min(z + 0.5, 4))} className="w-9 h-9 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center text-white transition-colors" data-testid="lightbox-zoom-in">
            <MagnifyingGlassPlus size={18} weight="bold" />
          </button>
          <button onClick={() => setZoom(z => Math.max(z - 0.5, 0.5))} className="w-9 h-9 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center text-white transition-colors" data-testid="lightbox-zoom-out">
            <MagnifyingGlassMinus size={18} weight="bold" />
          </button>
          <a href={src} download target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center text-white transition-colors" data-testid="lightbox-download">
            <DownloadSimple size={18} weight="bold" />
          </a>
          <button onClick={onClose} className="w-9 h-9 bg-white/10 hover:bg-red-500/80 rounded-lg flex items-center justify-center text-white transition-colors" data-testid="lightbox-close">
            <X size={20} weight="bold" />
          </button>
        </div>
      </div>

      {/* Image */}
      <div className="flex-1 flex items-center justify-center overflow-hidden relative" onClick={e => e.stopPropagation()}>
        {images.length > 1 && (
          <button onClick={prev} className="absolute left-4 z-10 w-12 h-12 bg-black/50 hover:bg-black/80 rounded-full flex items-center justify-center text-white transition-colors" data-testid="lightbox-prev">
            <CaretLeft size={28} weight="bold" />
          </button>
        )}
        <img
          src={src}
          alt={caption || `Image ${index + 1}`}
          className="max-h-full max-w-full object-contain transition-transform duration-200"
          style={{ transform: `scale(${zoom})` }}
          draggable={false}
          data-testid="lightbox-image"
        />
        {images.length > 1 && (
          <button onClick={next} className="absolute right-4 z-10 w-12 h-12 bg-black/50 hover:bg-black/80 rounded-full flex items-center justify-center text-white transition-colors" data-testid="lightbox-next">
            <CaretRight size={28} weight="bold" />
          </button>
        )}
      </div>

      {/* Caption */}
      {caption && (
        <div className="px-4 py-2 text-center flex-shrink-0">
          <p className="text-white/80 text-sm">{caption}</p>
        </div>
      )}

      {/* Thumbnails */}
      {images.length > 1 && (
        <div className="flex items-center justify-center gap-2 px-4 py-3 overflow-x-auto flex-shrink-0" onClick={e => e.stopPropagation()}>
          {images.map((img, i) => {
            const thumbSrc = typeof img === 'string' ? img : img?.url || img?.src || '';
            return (
              <button key={i} onClick={() => { setIndex(i); setZoom(1); }}
                className={`w-14 h-14 rounded border-2 overflow-hidden flex-shrink-0 transition-all ${i === index ? 'border-[#D95800] opacity-100 scale-110' : 'border-white/20 opacity-50 hover:opacity-80'}`}
                data-testid={`lightbox-thumb-${i}`}>
                <img src={thumbSrc} alt="" className="w-full h-full object-cover" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
